import React, { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import { apiErrorMessage } from "../lib/inventoryApi";
import { bersihkanCacheKategoriHarga } from "../lib/kategoriHarga";
import { ITEM_TYPE_TIGA, LABEL_ITEM_TYPE } from "../lib/faKonfigurasi";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Plus, Edit } from "lucide-react";
import { toast } from "sonner";
import Hint from "../components/etams/Hint";
import { useDaftar } from "../hooks/useDaftar";
import { PalangDaftar, ThUrut, Paginasi } from "../components/etams/PalangDaftar";

/* U22b (22 Sep 2026) — MASTER KATEGORI HARGA, Owner saja.
   Arahan Owner 21 Sep B1: kategori paket harga dikelola dari LAYAR — tambah, ganti
   nama, nonaktifkan (tidak ada hapus), urutkan. Vonis 22 Sep R1: tiap kategori baru
   WAJIB ditanya tiga hal tanpa nilai bawaan — jenis item yang dijual, boleh berjenjang
   harga per jumlah, minimum pesanan. K188: perubahan tidak berlaku surut ke FA lama.
   Sumber: GET/POST/PATCH /api/master/kategori-harga (router lapis pertama, RLS kedua). */

const KOSONG = { kode: "", label: "", urutan: "", aktif: true, item_type_sah: [], berjenjang: null, minimum_pesanan: "", catatan: "" };

export default function MasterKategoriHarga() {
  const [baris, setBaris] = useState([]);
  const [loading, setLoading] = useState(true);
  // Pola daftar seragam (28 Sep 2026) — master KECIL: cari/urut/halaman di klien; batas baris diawasi
  // `backend/tests/test_batas_master_kecil_lokal.py`.
  const daftar = useDaftar(baris, {
    kunciCari: ["label", "kode"],
    kolom: [{ kunci: "urutan", judul: "Urutan" }, { kunci: "kode", judul: "Kode" }, { kunci: "label", judul: "Label" },
      { kunci: "item_type_sah", judul: "Jenis item", ambil: (k) => (k.item_type_sah || []).map((t) => LABEL_ITEM_TYPE[t] || t).join(" · ") },
      { kunci: "berjenjang", judul: "Berjenjang", ambil: (k) => (k.berjenjang ? "pita jumlah" : "satu harga") },
      { kunci: "minimum_pesanan", judul: "Minimum (lembar)" },
      { kunci: "aktif", judul: "Aktif", ambil: (k) => (k.aktif ? "Aktif" : "Nonaktif") }],
  });
  const [edit, setEdit] = useState(null);          // null | {} (baru) | baris

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/master/kategori-harga", { params: { semua: 1 } });
      setBaris(Array.isArray(r.data) ? r.data : []);
    } catch (e) { toast.error(apiErrorMessage(e, "Gagal memuat kategori harga.")); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="master-kategori-harga">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold">
            Master Kategori Harga{" "}
            <Hint>Kategori paket harga yang dipilih customer (Budget, Sporty, …); kode tetap, label boleh diganti, dan kategori tidak dihapus melainkan dinonaktifkan. Perubahan hanya berlaku untuk FA baru.</Hint>
          </h1>
        </div>
        <Button onClick={() => setEdit({})} data-testid="kh-tambah"><Plus size={16} className="mr-1" />Kategori baru</Button>
      </div>

      <PalangDaftar daftar={daftar} placeholder="Cari kode / label…" testId="kh-palang"
                    ekspor={{ nama: "kategori_harga", kolom: daftar.kolom }} />
      <Card className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
            <tr>
              <ThUrut daftar={daftar} kunci="urutan" className="text-left px-3 py-2">Urutan</ThUrut>
              <ThUrut daftar={daftar} kunci="kode" className="text-left px-3 py-2">Kode</ThUrut>
              <ThUrut daftar={daftar} kunci="label" className="text-left px-3 py-2">Label (tampil)</ThUrut>
              <th className="text-left px-3 py-2">Jenis item yang dijual</th>
              <th className="text-left px-3 py-2">Berjenjang</th>
              <th className="text-right px-3 py-2">Minimum</th>
              <th className="text-left px-3 py-2">Aktif</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={8} className="px-3 py-6 text-center text-muted-foreground">Memuat…</td></tr>}
            {!loading && daftar.total === 0 && <tr><td colSpan={8} className="px-3 py-6 text-center text-muted-foreground">
              {daftar.cari.trim() ? "Tidak ada yang cocok." : "Belum ada kategori."}</td></tr>}
            {daftar.tampil.map((k) => (
              <tr key={k.kode} className={"border-t border-border " + (k.aktif ? "" : "opacity-60")} data-testid={`kh-baris-${k.kode}`}>
                <td className="px-3 py-2">{k.urutan}</td>
                <td className="px-3 py-2 font-mono text-xs">{k.kode}</td>
                <td className="px-3 py-2 font-medium">{k.label}</td>
                <td className="px-3 py-2 text-xs">{(k.item_type_sah || []).map((t) => LABEL_ITEM_TYPE[t] || t).join(" · ")}</td>
                <td className="px-3 py-2">{k.berjenjang ? <Badge variant="outline">pita jumlah</Badge> : <span className="text-muted-foreground">satu harga</span>}</td>
                <td className="px-3 py-2 text-right">{k.minimum_pesanan} lbr</td>
                <td className="px-3 py-2">{k.aktif ? <Badge>Aktif</Badge> : <Badge variant="secondary">Nonaktif</Badge>}</td>
                <td className="px-3 py-2 text-right">
                  <Button size="sm" variant="ghost" onClick={() => setEdit(k)} data-testid={`kh-ubah-${k.kode}`}><Edit size={14} /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <Paginasi daftar={daftar} testId="kh-paginasi" />
      </Card>

      {edit !== null && (
        <FormKategori baris={edit.kode ? edit : null} onClose={() => setEdit(null)}
                      onSaved={() => { setEdit(null); bersihkanCacheKategoriHarga(); load(); }} />
      )}
    </div>
  );
}

function FormKategori({ baris, onClose, onSaved }) {
  const baru = !baris;
  const [f, setF] = useState(baru ? KOSONG : {
    kode: baris.kode, label: baris.label, urutan: baris.urutan ?? 0, aktif: baris.aktif,
    item_type_sah: baris.item_type_sah || [], berjenjang: !!baris.berjenjang,
    minimum_pesanan: baris.minimum_pesanan ?? "", catatan: baris.catatan || "",
  });
  const [saving, setSaving] = useState(false);
  const upd = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const toggleJenis = (t) => setF((x) => ({
    ...x, item_type_sah: x.item_type_sah.includes(t) ? x.item_type_sah.filter((y) => y !== t) : [...x.item_type_sah, t],
  }));

  // R1: tiga hal ditanya, tanpa nilai bawaan — tombol simpan diam sampai ketiganya dijawab.
  const kurang = [];
  if (!f.item_type_sah.length) kurang.push("jenis item yang dijual");
  if (baru && f.berjenjang === null) kurang.push("boleh berjenjang atau tidak");
  if (f.minimum_pesanan === "" || Number(f.minimum_pesanan) < 1) kurang.push("minimum pesanan");
  if (!f.label || f.label.trim().length < 2) kurang.push("label");
  if (baru && !/^[A-Za-z0-9_]{2,40}$/.test(f.kode || "")) kurang.push("kode (huruf/angka/garis bawah, 2–40)");

  async function simpan() {
    setSaving(true);
    try {
      const badan = {
        label: f.label.trim(), urutan: Number(f.urutan) || 0, aktif: !!f.aktif,
        item_type_sah: ITEM_TYPE_TIGA.filter((t) => f.item_type_sah.includes(t)),
        berjenjang: !!f.berjenjang, minimum_pesanan: Number(f.minimum_pesanan), catatan: f.catatan || null,
      };
      if (baru) {
        await api.post("/master/kategori-harga", { kode: f.kode.trim().toUpperCase(), ...badan });
        toast.success(`Kategori ${f.kode.toUpperCase()} dibuat.`);
      } else {
        const r = await api.patch(`/master/kategori-harga/${baris.kode}`, badan);
        toast.success(r.data?._peringatan || `Kategori ${baris.kode} diperbarui.`);
      }
      onSaved();
    } catch (e) { toast.error(apiErrorMessage(e, "Gagal menyimpan kategori.")); }
    finally { setSaving(false); }
  }

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="kh-form">
        <DialogHeader><DialogTitle>{baru ? "Kategori harga baru" : `Ubah kategori ${baris.kode}`}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <Label>Kode {baru ? "*" : ""}</Label>
            <Input value={f.kode} disabled={!baru} onChange={(e) => upd("kode", e.target.value.toUpperCase())} placeholder="mis. PREMIUM" data-testid="kh-kode" />
            {!baru && <p className="text-[11px] text-muted-foreground mt-1">Kode tidak bisa diubah; ganti labelnya saja.</p>}
          </div>
          <div>
            <Label>Label yang tampil *</Label>
            <Input value={f.label} onChange={(e) => upd("label", e.target.value)} placeholder="mis. Premium" data-testid="kh-label" />
          </div>
          <div>
            <Label>Urutan</Label>
            <Input type="number" value={f.urutan} onChange={(e) => upd("urutan", e.target.value)} data-testid="kh-urutan" />
          </div>
          <div>
            <Label>Minimum pesanan (lembar) *</Label>
            <Input type="number" min={1} value={f.minimum_pesanan} onChange={(e) => upd("minimum_pesanan", e.target.value)} placeholder="tanpa bawaan — isi" data-testid="kh-minimum" />
          </div>
          <div className="col-span-2">
            <Label>Jenis item yang dijual kategori ini *</Label>
            <div className="mt-1 space-y-1">
              {ITEM_TYPE_TIGA.map((t) => (
                <label key={t} className="flex items-center gap-2 text-sm">
                  <Checkbox checked={f.item_type_sah.includes(t)} onCheckedChange={() => toggleJenis(t)} data-testid={`kh-jenis-${t}`} />
                  {LABEL_ITEM_TYPE[t]}
                </label>
              ))}
            </div>
          </div>
          <div className="col-span-2">
            <Label>Harga berjenjang per jumlah? *</Label>
            <div className="mt-1 flex gap-4 text-sm">
              <label className="flex items-center gap-2"><input type="radio" name="kh-berjenjang" checked={f.berjenjang === false} onChange={() => upd("berjenjang", false)} data-testid="kh-berjenjang-tidak" /> Tidak — satu harga per jenis</label>
              <label className="flex items-center gap-2"><input type="radio" name="kh-berjenjang" checked={f.berjenjang === true} onChange={() => upd("berjenjang", true)} data-testid="kh-berjenjang-ya" /> Ya — pita jumlah (mis. 300–499, 500 ke atas)</label>
            </div>
          </div>
          <div className="col-span-2">
            <Label>Catatan</Label>
            <Textarea rows={2} value={f.catatan} onChange={(e) => upd("catatan", e.target.value)} />
          </div>
          <label className="col-span-2 flex items-center gap-2 text-sm">
            <Checkbox checked={!!f.aktif} onCheckedChange={(v) => upd("aktif", !!v)} data-testid="kh-aktif" /> Aktif (ditawarkan untuk FA dan harga baru)
          </label>
          {kurang.length > 0 && (
            <p className="col-span-2 text-xs text-warning-soft-foreground bg-warning-soft border border-warning-soft-border rounded px-3 py-2" data-testid="kh-kurang">
              Belum dijawab: {kurang.join("; ")}.
            </p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Batal</Button>
          <Button onClick={simpan} disabled={saving || kurang.length > 0} data-testid="kh-simpan">{saving ? "Menyimpan…" : "Simpan"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
